import React, { useCallback, useEffect } from 'react';

import { injectIntl, InjectedIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Badge from '@atlaskit/badge';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { SidebarContent } from 'src/components/sidebar';
import { createChildWrapper } from 'src/components/sidebar/src/sidebar';
import { updateSidebarState } from 'src/redux/sidebar';
import { SidebarType } from 'src/redux/sidebar/types';
import * as styles from 'src/sections/global/components/sidebar/sidebar-tabs.style';
import messages from 'src/sections/global/components/sidebar-tabs.i18n';

export type SidebarTabDetails = {
  conflictsCount: number;
  sidebarTabIndex: number;
  untruncatedFileCount: number;
};

export type SidebarTabsProps = {
  intl: InjectedIntl;
  expandedContent: SidebarContent[];
  expandedFileContent: JSX.Element;
  sidebarType: SidebarType;
  onSelectAnalytics?: () => (index: number) => void;
  expandedActivityContent?: JSX.Element;
  sidebarTabsDetails: () => SidebarTabDetails;
  skipDetailsTab?: boolean;
};

const SidebarTabs = ({ sidebarTabsDetails, ...props }: SidebarTabsProps) => {
  const {
    expandedContent,
    expandedFileContent,
    sidebarType,
    onSelectAnalytics = () => (_index: number) => {
      /* noop analytics call */
    },
    expandedActivityContent,
    intl,
    skipDetailsTab,
  } = props;

  const dispatch = useDispatch();
  useEffect(() => {
    return () => {
      dispatch(
        updateSidebarState({
          isOpen: true,
          tabIndex: 0,
          sidebarType,
        })
      );
    };
  }, [dispatch, sidebarType]);

  const { conflictsCount, untruncatedFileCount, sidebarTabIndex } =
    sidebarTabsDetails();

  const analyticsCallBack: (index: number) => void = onSelectAnalytics();

  const onSelect = useCallback(
    (selectedIndex: number) => {
      analyticsCallBack(selectedIndex);
      dispatch(
        updateSidebarState({
          isOpen: true,
          tabIndex: selectedIndex,
          sidebarType,
        })
      );
    },
    [analyticsCallBack, dispatch, sidebarType]
  );

  return (
    <styles.StickyTabsWrapper>
      <Tabs
        id="sidebar-tabs"
        selected={sidebarTabIndex}
        onChange={index => onSelect(index)}
      >
        {/* This div looks superfluous but is actually needed to make the styling on the tabs work*/}
        <div>
          <TabList>
            {skipDetailsTab ? null : (
              <Tab testId="sidebar-tab-details">
                {intl.formatMessage(messages.details)}
              </Tab>
            )}
            <Tab testId="sidebar-tab-files">
              <styles.SidebarTabItem>
                <styles.FileTreeTabLabel>
                  {intl.formatMessage(messages.files)}
                </styles.FileTreeTabLabel>
                <Badge>{untruncatedFileCount}</Badge>
                {conflictsCount > 0 && (
                  <Tooltip
                    position="bottom"
                    content={intl.formatMessage(messages.mergeConflicts, {
                      total: conflictsCount,
                    })}
                  >
                    <styles.MergeConflictIcon>
                      <WarningIcon
                        label={intl.formatMessage(messages.mergeConflicts, {
                          total: conflictsCount,
                        })}
                        primaryColor={token('color.icon.warning', colors.Y500)}
                      />
                    </styles.MergeConflictIcon>
                  </Tooltip>
                )}
              </styles.SidebarTabItem>
            </Tab>
            {expandedActivityContent !== undefined ? (
              <Tab testId="sidebar-tab-activity">
                {intl.formatMessage(messages.activity)}
              </Tab>
            ) : null}
          </TabList>
        </div>
        {skipDetailsTab ? null : (
          <TabPanel>
            <styles.ExpandedSidebarContent key="sidebar-tab-wrapper-0">
              {expandedContent.map(createChildWrapper('expanded'))}
            </styles.ExpandedSidebarContent>
          </TabPanel>
        )}
        <TabPanel>
          <styles.ExpandedSidebarContent key="sidebar-tab-wrapper-1">
            <styles.SidebarTab>{expandedFileContent}</styles.SidebarTab>
          </styles.ExpandedSidebarContent>
        </TabPanel>
        {expandedActivityContent !== undefined ? (
          <TabPanel>
            <styles.ExpandedSidebarContent key="sidebar-tab-wrapper-2">
              <styles.SidebarTab>{expandedActivityContent}</styles.SidebarTab>
            </styles.ExpandedSidebarContent>
          </TabPanel>
        ) : null}
      </Tabs>
    </styles.StickyTabsWrapper>
  );
};

export default injectIntl(SidebarTabs);
